import { useEffect, useState } from 'react';
import { Button, Form, InputGroup, Modal, Stack } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { useHistory, useParams } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';
import useValidatePassword from 'src/hooks/useValidatePassword';

import LoginContainer from './LoginContainer';
import { ResetPasswordFormData } from './ResetPasswordForm';

const ResetPasswordWithOTP = () => {
  const { tPages, tCommon, language } = useUiTranslation();
  const history = useHistory();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const { email } = useParams<{ email: string }>();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
    control,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      otp: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const password = watch('password');

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await Api.remote.resetPassword(
        email,
        data.otp,
        data.password,
        data.confirmPassword,
      );

      Api.alertBox({
        title: tCommon('bootbox.title.Success'),
        message: tPages('changePassword.resetPasswordSuccessfully'),
        locale: getLocaleCode(language as Language),
        type: 'success',
        callback: () => {
          history.replace('/');
        },
      });
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const [countdownSeconds, setCountdownSeconds] = useState(30);

  useEffect(() => {
    if (countdownSeconds > 0) {
      const intervalId = setInterval(() => {
        setTimeout(() => {
          setCountdownSeconds((prevSeconds) => prevSeconds - 1);
        }, 500);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [countdownSeconds]);

  const handleResendCode = async () => {
    try {
      await Api.remote.sendResetCode(email);
      setCountdownSeconds(30);
    } catch (err) {
      const errorMessage = handleHttpRequestError(err);
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: errorMessage,
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit(handleResetPassword)}>
        <Modal.Body>
          <Stack className="text-center">
            <h2>{tPages('changePassword.title')}</h2>
            <p className="mb-0">
              <small>{tPages('changePassword.sendOTPCode')}</small>
            </p>
            <small>
              {
                <Stack
                  direction="horizontal"
                  className="justify-content-center"
                  gap={1}
                >
                  <span>{tPages('loginLogout.didNotReceiveCode')}</span>
                  {countdownSeconds > 0 ? (
                    <Trans
                      i18nKey="ui.pages.loginLogout.resendCodeInSeconds"
                      values={{ countdownSeconds }}
                      components={[<strong>{countdownSeconds}</strong>]}
                    />
                  ) : (
                    <Button
                      variant="link"
                      className="p-0 text-primary"
                      size="sm"
                      onClick={handleResendCode}
                    >
                      {tPages('loginLogout.resendCode')}
                    </Button>
                  )}
                </Stack>
              }
            </small>
          </Stack>

          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{tPages('changePassword.otpCode')}</strong>
            </Form.Label>

            <Controller
              name="otp"
              control={control}
              defaultValue=""
              rules={{ required: tPages('changePassword.otpCodeRequired') }}
              render={({ field }) => (
                <OtpInput
                  inputType="tel"
                  containerStyle={{ display: 'flex', justifyContent: 'center' }}
                  numInputs={6}
                  renderSeparator={<span className="mx-2">-</span>}
                  inputStyle={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #ced4da',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                  }}
                  renderInput={(props) => <input {...props} />}
                  shouldAutoFocus
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="text-danger">{errors.otp?.message}</div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{tPages('changePassword.newPassword')}</strong>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowPassword ? 'text' : 'password'}
                placeholder={tPages('changePassword.newPasswordPlaceholder')}
                autoComplete="off"
                {...register('password', {
                  required: tPages('changePassword.passwordRequired'),
                  validate: useValidatePassword(),
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowPassword(!isShowPassword)}
                className="pointer"
              >
                <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
              </InputGroup.Text>
            </InputGroup>
            <div className="text-danger">
              {errors.password?.type === 'required' ? (
                <p>{tPages('changePassword.newPasswordRequired')}</p>
              ) : (
                <ul className="list-unstyled">
                  {errors.password?.types?.minLength && (
                    <li>{errors.password.message}</li>
                  )}
                  {errors.password?.types?.hasUpperCase && (
                    <li>{errors.password.types.hasUpperCase}</li>
                  )}
                  {errors.password?.types?.hasLowerCase && (
                    <li>{errors.password.types.hasLowerCase}</li>
                  )}
                  {errors.password?.types?.hasNumber && (
                    <li>{errors.password.types.hasNumber}</li>
                  )}
                  {errors.password?.types?.hasSpecialChar && (
                    <li>{errors.password.types.hasSpecialChar}</li>
                  )}
                </ul>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{tPages('changePassword.confirmPassword')}</strong>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowConfirmPassword ? 'text' : 'password'}
                placeholder={tPages(
                  'changePassword.confirmPasswordPlaceholder',
                )}
                autoComplete="off"
                {...register('confirmPassword', {
                  required: tPages('changePassword.confirmPasswordRequired'),
                  validate: (value) =>
                    value === password ||
                    tPages('changePassword.passwordsDoNotMatch'),
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                className="pointer"
              >
                <FontAwesomeIcon
                  icon={isShowConfirmPassword ? faEye : faEyeSlash}
                />
              </InputGroup.Text>
            </InputGroup>
            {<p className="text-danger ">{errors.confirmPassword?.message}</p>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-end">
          <Button
            variant="primary-100"
            onClick={() => {
              history.goBack();
            }}
          >
            {tPages('changePassword.back')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            data-test="connect-camera"
          >
            {isSubmitting
              ? tPages('changePassword.submitting')
              : tPages('changePassword.continue')}
          </Button>
        </Modal.Footer>
      </Form>
    </LoginContainer>
  );
};

export default ResetPasswordWithOTP;
